<template>
  <div class="ds-page">
    <div class="mb-3" style="font-size: 12px">
      <a class="text-dark mr-3" style="cursor: pointer" @click="back">‹ Back</a>
        <span style="color: #D1D1D1; margin-right: 16px; font-weight: bold">|</span>
      <span style="color: #9F9F9F;">Data Submission › </span><b style="color: #2D2D2D;">Reminder Settings</b>
    </div>
    <!-- Info banner (UR-11.c AC4): memakai nilai N yang dipilih -->
    <div class="rs-banner" role="note">
      Each run checks the last {{ checkN }} completed PO month(s) (you can change this below). Oil Makers with any month
      above the tolerance get one reminder per day during the send window, listing those months. A month stops being
      reminded once it is within tolerance or moves out of the {{ checkN }}-month range.
    </div>
    <div class="ds-tabs">
      <button class="ds-tab" :class="{ active: tab === 'oil' }" @click="tab = 'oil'">Oil Maker</button>
      <button class="ds-tab" disabled>Label Maker </button>
      <!-- <span class="ds-soon">Coming Soon</span> -->
    </div>

    <div v-if="loading" class="ds-card ds-muted text-center">Loading...</div>
    <div v-else-if="!s" class="ds-card ds-muted text-center">{{ tabName }} reminder settings are not available yet.</div>

    <template v-else>
      <div class="ds-card p-0">
        <!-- Active -->
        <div class="row-s">
          <div class="col-l"><b>{{ tabName }} Reminder</b><span class="ds-sub">These settings apply to {{ tabName }} only</span></div>
          <div class="col-r">
            <label class="switch mb-0"><input type="checkbox" v-model="s.active" /><span class="slider"></span></label>
            <span :class="s.active ? 'text-primary' : 'ds-muted'" class="ml-2">{{ s.active ? "Active" : "Inactive" }}</span>
            <!-- <span v-if="meta.updatedBy" class="ds-sub mt-2">Last updated by {{ meta.updatedBy }} · {{ meta.updatedAt }}</span> -->
          </div>
        </div>

        <!-- Saat Inactive: field lain redup tapi tetap bisa diubah -->
        <div :class="{ dim: !s.active }">
          <!-- Schedule -->
          <!-- Send window (UR-11.b) + Rolling check period (UR-11.c) -->
          <div class="row-s">
            <div class="col-l"><b>Automatic send window</b>
              <span class="ds-sub">Reminders are checked daily within this window</span></div>
            <div class="col-r">
              <div class="d-flex align-items-center flex-wrap" style="gap: 12px">
                Send between day
                <select class="fig-select fig-select-sm" :class="{ 'is-error': errors.window }" v-model="s.startDay">
                  <option v-for="d in dayOptions" :key="d" :value="d">{{ d }}</option>
                </select>
                –
                <select class="fig-select fig-select-sm" :class="{ 'is-error': errors.window }" v-model="s.endDay">
                  <option v-for="d in dayOptions" :key="d" :value="d">{{ d }}</option>
                </select>
                of each month, at
                <select class="fig-select fig-select-md" :class="{ 'is-error': errors.hour }" v-model="s.hour">
                  <option v-for="h in hourOptions" :key="h" :value="h">{{ hourLabel(h) }}</option>
                </select>
                WIB
              </div>
              <div v-if="errors.window || errors.hour" class="ds-hint text-danger mb-0 mt-2">{{ errors.window || errors.hour }}</div>
              <div class="fig-hints mt-2">
                {{ tabName }}s above the tolerance receive one reminder per day within this window. Sending stops once
                the {{ tabName }} is within tolerance.
              </div>

              <div class="d-flex align-items-center flex-wrap mt-3" style="gap: 12px">
                Check the last
                <select class="fig-select fig-select-sm" v-model="s.checkPeriod">
                  <option v-for="n in checkPeriodOptions" :key="n" :value="n">{{ n }}</option>
                </select>
                month(s) of QR PO
              </div>
              <div v-if="errors.checkPeriod" class="ds-hint text-danger mb-0 mt-2">{{ errors.checkPeriod }}</div>
              <div class="fig-hints mt-2">
                Each run checks the last {{ checkN }} completed PO months. Months already within tolerance are skipped.
                The current month is never included.
              </div>

              <div v-if="!errors.window" class="fig-note mt-3">
                Example: on <b class="note-hl">{{ example.when }}</b>, QR PO for
                <b class="note-dark">{{ example.months }}</b> is checked daily. {{ tabName }}s with any month above the
                tolerance receive 1 reminder per day listing those months.
              </div>
            </div>
          </div>

          <!-- Condition -->
          <div class="row-s">
            <div class="col-l"><b>Condition</b></div>
            <div class="col-r">
              <!-- UR-11.a: input bebas 1–100%, tanpa toggle Condition (BR-17) -->
              <div class="d-flex align-items-center" style="gap: 10px">
                <label for="rs-tolerance" class="mb-0">Data Discrepancy Tolerance</label>
                <span class="tol-input" :class="{ 'is-error': errors.tolerance }">
                  <input id="rs-tolerance" type="number" inputmode="numeric" :min="options.toleranceMin"
                    :max="options.toleranceMax" step="1" v-model.number="s.tolerance" />
                  <span class="tol-suffix">%</span>
                </span>
              </div>
              <div v-if="errors.tolerance" class="ds-hint text-danger mb-0 mt-2">{{ errors.tolerance }}</div>
              <div v-else class="fig-hints mt-2">
                Example: If the QR PO for a month is 100,000 and the tolerance is {{ toleranceNum }}%, the SFTP data must
                contain at least {{ minQr }} QR codes. Otherwise, that month is included in the daily reminder within
                the send window until this is met.
              </div>
            </div>
          </div>

          <!-- Channel -->
          <div class="row-s">
            <div class="col-l"><b>Default channel</b></div>
            <div class="col-r">
              <label class="mb-0"><input type="checkbox" v-model="s.email" /> Email</label>
              <div v-if="errors.channel" class="ds-hint text-danger">{{ errors.channel }}</div>
            </div>
          </div>

          <!-- Template -->
          <div class="row-s">
            <div class="col-l"><b>Message template</b>
              <span class="ds-sub" style="font-size: 12px;">Variables: {{ variablesText }}</span>
              <span class="ds-sub" style="font-size: 12px;">{period} is filled with all months listed in the email</span></div>
            <div class="col-r">
              <label class="ds-label">Subject (email)</label>
              <input class="ds-input" :class="{ 'is-error': errors.subject }" v-model="s.subject" :maxlength="options.subjectMax" />
              <div class="ds-hint d-flex justify-content-between">
                <span class="text-danger">{{ errors.subject }}</span>
                <span>{{ s.subject.length }} / {{ options.subjectMax }}</span>
              </div>
              <label class="ds-label">Message (email)</label>
              <textarea class="ds-input" :class="{ 'is-error': errors.message }" rows="5" v-model="s.message" :maxlength="options.messageMax"></textarea>
              <div class="ds-hint d-flex justify-content-between">
                <span class="text-danger">{{ errors.message }}</span>
                <!-- <span>{{ s.message.length }} / {{ options.messageMax }}</span> -->
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end" style="gap: 8px">
        <button class="ds-btn ds-btn-danger-o" :disabled="saving" @click="back">Cancel</button>
        <button class="ds-btn ds-btn-primary" :disabled="saving || !isDirty || hasError" @click="save">{{ saving ? "Saving..." : "Save" }}</button>
      </div>
    </template>
  </div>
</template>

<script>
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const ENDPOINTS = {
  oil: "/api/data-submission/oil-maker/reminder-settings",
  label: null,
};

export default {
  name: "ReminderSettings",
  data() {
    return {
      tab: "oil",
      loading: false,
      saving: false,
      s: null,
      original: "",
      meta: { updatedBy: "", updatedAt: "" },
      options: {
        dayMin: 1, dayMax: 31, hourMin: 0, hourMax: 23,
        checkPeriodMin: 1, checkPeriodMax: 12, toleranceMin: 1, toleranceMax: 100,
        windowMaxDays: null, variables: [], subjectMax: 150, messageMax: 2000,
      },
      checkPeriods: [], // YYYY-MM dari BE, untuk contoh bulan saat check_months belum diubah
      serverErrors: {}, // error validasi 400 dari BE, dihapus saat form diubah
    };
  },
  computed: {
    endpoint() { return ENDPOINTS[this.tab]; },
    tabName() { return this.tab === "oil" ? "Oil Maker" : "Label Maker"; },
    dayOptions() {
      const { dayMin, dayMax } = this.options;
      return Array.from({ length: dayMax - dayMin + 1 }, (_, i) => dayMin + i);
    },
    hourOptions() {
      const { hourMin, hourMax } = this.options;
      return Array.from({ length: hourMax - hourMin + 1 }, (_, i) => hourMin + i);
    },
    checkPeriodOptions() {
      const { checkPeriodMin, checkPeriodMax } = this.options;
      return Array.from({ length: checkPeriodMax - checkPeriodMin + 1 }, (_, i) => checkPeriodMin + i);
    },
    checkN() { return this.s ? Number(this.s.checkPeriod) : 3; },
    variablesText() { return this.options.variables.map((v) => `{${v}}`).join(" "); },
    toleranceNum() { return this.s ? Number(this.s.tolerance) : NaN; },
    toleranceValid() {
      const n = this.toleranceNum;
      const raw = this.s ? String(this.s.tolerance).trim() : "";
      return raw !== "" && Number.isInteger(n) && n >= this.options.toleranceMin && n <= this.options.toleranceMax;
    },
    minQr() { return Math.ceil((100000 * (100 - this.toleranceNum)) / 100).toLocaleString("en-US"); },
    // Contoh UR-11.b AC5: bulan eksekusi = bulan ini, atau bulan depan bila window bulan ini sudah lewat.
    // Jendela periode = M-1 s/d M-N (BR-21), bulan berjalan tidak termasuk.
    example() {
      const now = new Date();
      const run = new Date(now.getFullYear(), now.getMonth() + (now.getDate() > this.s.endDay ? 1 : 0), 1);
      const isCurrentMonth = run.getMonth() === now.getMonth();
      let months = [];
      if (isCurrentMonth && this.checkPeriods.length === this.checkN) {
        // check_periods dari BE (oldest first) berlaku untuk bulan ini dan check_months yang tersimpan
        months = this.checkPeriods.map((p) => { const [y, m] = p.split("-").map(Number); return new Date(y, m - 1, 1); });
      } else {
        for (let i = this.checkN; i >= 1; i--) months.push(new Date(run.getFullYear(), run.getMonth() - i, 1));
      }
      const sameYear = months.every((m) => m.getFullYear() === months[0].getFullYear());
      const names = months.map((m) => (sameYear ? MONTHS[m.getMonth()] : `${MONTHS[m.getMonth()]} ${m.getFullYear()}`));
      const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
      const days = this.s.startDay === this.s.endDay ? `${this.s.startDay}` : `${this.s.startDay}–${this.s.endDay}`;
      return {
        when: `${days} ${MONTHS[run.getMonth()]} · ${this.hourLabel(this.s.hour)} WIB`,
        months: sameYear ? `${list} ${months[0].getFullYear()}` : list,
      };
    },
    errors() {
      if (!this.s) return {};
      const se = this.serverErrors;
      const start = Number(this.s.startDay);
      const end = Number(this.s.endDay);
      const maxDays = this.options.windowMaxDays; // null = belum ada batas (PRD TBD)
      let window = "";
      if (end < start) window = "End day must be on or after start day";
      else if (maxDays && end - start + 1 > maxDays) window = `Send window can be at most ${maxDays} days`;
      return {
        window: window || se.send_start_day || se.send_end_day || "",
        hour: se.send_hour || "",
        checkPeriod: se.check_months || "",
        tolerance: this.toleranceValid
          ? se.tolerance || ""
          : `Enter a whole number between ${this.options.toleranceMin} and ${this.options.toleranceMax}`,
        subject: this.s.subject.trim() ? se.subject || "" : "Required",
        message: this.s.message.trim() ? se.message || "" : "Required",
        channel: this.s.active && !this.s.email ? "Select at least one channel." : se.channel_email || "",
      };
    },
    hasError() { return Object.values(this.errors).some(Boolean); },
    isDirty() { return !!this.s && JSON.stringify(this.s) !== this.original; },
  },
  watch: {
    tab() { this.fetchSettings(); },
    s: {
      deep: true,
      handler() { if (Object.keys(this.serverErrors).length) this.serverErrors = {}; },
    },
  },
  mounted() { this.fetchSettings(); },
  beforeRouteLeave(to, from, next) {
    if (!this.isDirty || this.saving || !this.$swal) return next();
    this.$swal.fire({
      title: "Discard changes?",
      text: "You have unsaved changes. Leave this page without saving?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Discard",
      cancelButtonText: "Keep editing",
    }).then((r) => next(r.isConfirmed));
  },
  methods: {
    hourLabel(h) { return String(h).padStart(2, "0") + ":00"; },
    back() { this.$router.push({ name: "DataSubmissionOilMaker" }); },

    // ---------- API ----------
    async fetchSettings() {
      this.s = null;
      if (!this.endpoint) return;
      this.loading = true;
      try {
        const res = await this.$http.get(this.endpoint);
        const d = res.data.data;
        const o = d.options || {};
        const range = (v, min, max) => (v && !Array.isArray(v) && v.min !== undefined ? v : { min, max });
        const dayOpt = range(o.send_day, 1, 28);
        const checkOpt = range(o.check_months, 1, 12);
        const tolOpt = range(o.tolerance, 1, 100);
        this.options = {
          dayMin: dayOpt.min,
          dayMax: dayOpt.max,
          hourMin: o.send_hour ? o.send_hour.min : 0,
          hourMax: o.send_hour ? o.send_hour.max : 23,
          checkPeriodMin: checkOpt.min,
          checkPeriodMax: checkOpt.max,
          toleranceMin: tolOpt.min,
          toleranceMax: tolOpt.max,
          windowMaxDays: o.send_window_max_days || null,
          variables: o.variables || [],
          subjectMax: o.subject_max || 150,
          messageMax: o.message_max || 2000,
        };
        this.meta = { updatedBy: d.updated_by, updatedAt: d.updated_at ? d.updated_at.slice(0, 16) : "" };
        this.checkPeriods = Array.isArray(d.check_periods) ? d.check_periods : [];
        const startDay = d.send_start_day != null ? d.send_start_day : 1;
        this.s = {
          active: !!d.is_active,
          startDay,
          endDay: d.send_end_day != null ? d.send_end_day : startDay,
          hour: d.send_hour,
          checkPeriod: d.check_months != null ? d.check_months : 3,
          tolerance: d.tolerance != null ? d.tolerance : 25,
          email: !!d.channel_email,
          subject: d.subject || "",
          message: d.message || "",
        };
        this.original = JSON.stringify(this.s);
      } catch (err) {
        this.notify("error", "Failed to load settings", err);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.hasError) return;
      const payload = {
        is_active: this.s.active,
        send_start_day: this.s.startDay,
        send_end_day: this.s.endDay,
        send_hour: this.s.hour,
        check_months: this.s.checkPeriod,
        tolerance: this.toleranceNum,
        channel_email: this.s.email,
        subject: this.s.subject,
        message: this.s.message,
      };
      this.saving = true;
      try {
        const res = await this.$http.put(this.endpoint, payload);
        await this.fetchSettings();
        this.notify("success", (res.data && res.data.message) || `${this.tabName} reminder settings saved`);
      } catch (err) {
        const body = err && err.response && err.response.data;
        if (err.response && err.response.status === 400 && body && body.data && typeof body.data === "object") {
          // Error per field: string atau { message, variables[] } (mis. "Unknown variable: {gap_count}")
          const se = {};
          Object.entries(body.data).forEach(([k, v]) => {
            se[k] = typeof v === "string" ? v : v && v.message
              ? (v.variables && v.variables.length ? `${v.message}: ${v.variables.join(", ")}` : v.message)
              : "";
          });
          this.serverErrors = se;
        }
        this.notify("error", "Failed to save", err);
      } finally {
        this.saving = false;
      }
    },
    notify(icon, title, err) {
      if (!this.$swal) return;
      const body = err && err.response && err.response.data;
      let text = body && body.message;
      if (body && body.data && typeof body.data === "object") {
        const lines = Object.values(body.data).filter((v) => typeof v === "string");
        if (lines.length) text = lines.join("\n");
      }
      this.$swal.fire({ title, text, icon, timer: icon === "success" ? 1200 : undefined, showConfirmButton: icon !== "success" });
    },
  },
};
</script>

<style src="./ds-common.css"></style>
<style scoped>
.row-s { display: flex; padding: 20px 24px; border-bottom: 1px solid #eef1f4; }
.row-s:last-child { border-bottom: 0; }
.dim { opacity: .5; }
.col-l { width: 240px; flex-shrink: 0; padding-right: 16px; color: #2D2D2D; font-size: 13px; font-weight: 600; }
.col-r { flex: 1; }

.ds-tab:disabled { cursor: not-allowed; color: #C4CBCD; }
.ds-soon { margin-left: 6px; padding: 1px 6px; border-radius: 8px; background: #EDF1F2; font-size: 10px; color: #9aa4ad; }
.ds-input.is-error { border-color: #F92A2A; }
.fig-select.is-error { border-color: #F92A2A; }

/* Info banner halaman (UR-11.c AC4) */
.rs-banner { max-width: 1050px; background: #eef3fd; border: 1px solid #d6e1f8; color: #2f4068; border-radius: 8px;
  padding: 12px 14px; margin-bottom: 16px; font-size: 13px; line-height: 1.5; }

/* Input tolerance + suffix % (UR-11.a) */
.tol-input { display: inline-flex; align-items: stretch; height: 36px; border: 1px solid #d9dee3; border-radius: 6px; overflow: hidden; }
.tol-input.is-error { border-color: #F92A2A; }
.tol-input input { width: 72px; border: 0; padding: 0 10px; text-align: right; font-size: 13px; font-weight: 700; color: #222; outline: none; }
.tol-suffix { display: flex; align-items: center; padding: 0 12px; background: #f4f5f7; border-left: 1px solid #d9dee3; color: #5b616a; }

.switch { position: relative; display: inline-block; width: 36px; height: 20px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; inset: 0; background: #ccd2d8; border-radius: 20px; cursor: pointer; transition: .2s; }
.slider:before { content: ""; position: absolute; height: 14px; width: 14px; left: 3px; top: 3px; background: #fff; border-radius: 50%; transition: .2s; }
.switch input:checked + .slider { background: #3b7ddd; }
.switch input:checked + .slider:before { transform: translateX(16px); }

.fig-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: 36px;
  padding: 0 32px 0 14px;
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='8' viewBox='0 0 10 8'%3E%3Cpath d='M0 0h10L5 8z' fill='%23222'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  border: 1px solid #d9dee3;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #222;
  cursor: pointer;
}
.fig-select:focus { outline: none; border-color: #1f4aa0; }
.fig-select-sm { width: 68px; }
.fig-select-md { width: 94px; }

.fig-note {
  display: block;
  border: 0;
  background: #f1f3f2;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 12px;
  line-height: 1.6;
  color: #6b6f72;
}
.note-dark { color: #222; font-weight: 700; }
.note-hl { color: #1f4aa0; font-weight: 700; }
.fig-hints {
  display: block;
  border: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #9F9F9F;
}
.text-primary { color: #267CE5 !important; }
</style>